import type { FamilyBundle, Person } from "../models/types";
import { birthOrderSort } from "../utils/personDisplay";
import { isSundiusCousin, shuffle } from "../utils/calendar";

export type BirthOrderChallenge = "auntsUncles" | "cousins" | "kids";

export function canonicalPersonIds(
  bundle: FamilyBundle,
  challenge: BirthOrderChallenge,
): string[] {
  switch (challenge) {
    case "auntsUncles":
      return bundle.meta.sibling_order
        .map((id) => bundle.people.find((p) => p.person_id === id))
        .filter((p): p is Person => !!p && !p.is_deceased)
        .map((p) => p.person_id);
    case "cousins":
      return bundle.people
        .filter(isSundiusCousin)
        .sort(birthOrderSort)
        .map((p) => p.person_id);
    case "kids":
      return bundle.people
        .filter(
          (p) =>
            p.generation === 3 &&
            !p.is_partner &&
            !p.is_deceased &&
            !p.is_expected,
        )
        .sort(birthOrderSort)
        .map((p) => p.person_id);
  }
}

export function shuffledBirthOrderChallenge(
  bundle: FamilyBundle,
  challenge: BirthOrderChallenge,
): Person[] {
  const ids = canonicalPersonIds(bundle, challenge);
  const people = ids
    .map((id) => bundle.people.find((p) => p.person_id === id))
    .filter(Boolean) as Person[];
  if (people.length <= 1) return people;
  let result = shuffle(people);
  let attempts = 0;
  while (result.map((p) => p.person_id).join() === ids.join() && attempts < 8) {
    result = shuffle(people);
    attempts += 1;
  }
  return result;
}

export interface ParentCouple {
  id: string;
  parentIds: string[];
  label: string;
}

export function parentCoupleFrom(
  parentIds: string[],
  people: Person[],
): ParentCouple | null {
  const cleaned = [...new Set(parentIds.filter(Boolean))].sort();
  if (cleaned.length === 0) return null;
  const parents = cleaned
    .map((id) => people.find((p) => p.person_id === id))
    .filter(Boolean) as Person[];
  const ordered = [...parents].sort((a, b) => {
    if (!!a.is_partner !== !!b.is_partner) return a.is_partner ? 1 : -1;
    return a.display_name.localeCompare(b.display_name, undefined, { sensitivity: "base" });
  });
  const names = ordered.map((p) => p.display_name);
  let label = "Unknown parents";
  if (names.length === 1) label = names[0];
  else if (names.length >= 2) label = `${names[0]} & ${names[1]}`;
  return { id: cleaned.join("|"), parentIds: cleaned, label };
}

export interface CousinsKidsIdentifyRound {
  id: string;
  kidId: string;
  correctCoupleId: string;
  coupleChoices: ParentCouple[];
  nameChoiceIds: string[];
}

const NAME_CHOICE_COUNT = 4;

export function buildCousinsKidsIdentifyRounds(
  bundle: FamilyBundle,
): CousinsKidsIdentifyRound[] {
  const kidIds = canonicalPersonIds(bundle, "kids");
  const kids = kidIds
    .map((id) => bundle.people.find((p) => p.person_id === id))
    .filter(Boolean) as Person[];
  if (kids.length < 2) return [];

  const allCouples = kids
    .map((kid) => parentCoupleFrom(kid.parent_ids, bundle.people))
    .filter(Boolean) as ParentCouple[];
  const uniqueCouples = new Map<string, ParentCouple>();
  for (const couple of allCouples) uniqueCouples.set(couple.id, couple);
  const couplePool = [...uniqueCouples.values()].sort((a, b) =>
    a.label.localeCompare(b.label),
  );

  return shuffle(kids)
    .map((kid) => {
      const correct = parentCoupleFrom(kid.parent_ids, bundle.people);
      if (!correct) return null;

      const coupleChoices = [correct];
      for (const distractor of shuffle(couplePool.filter((c) => c.id !== correct.id))) {
        if (coupleChoices.length >= Math.min(4, couplePool.length)) break;
        coupleChoices.push(distractor);
      }

      const nameIds = [kid.person_id];
      for (const distractor of shuffle(kidIds.filter((id) => id !== kid.person_id))) {
        if (nameIds.length >= Math.min(NAME_CHOICE_COUNT, kidIds.length)) break;
        nameIds.push(distractor);
      }

      return {
        id: kid.person_id,
        kidId: kid.person_id,
        correctCoupleId: correct.id,
        coupleChoices: shuffle(coupleChoices),
        nameChoiceIds: shuffle(nameIds),
      };
    })
    .filter(Boolean) as CousinsKidsIdentifyRound[];
}
